// THE LINE-ITEM BULK ROW (owner, 2026-09-29 and 2026-09-30 — `docs/decisions.md`
// D-228 to D-232 and D-242). It appears once at least one line's box is
// checked, at every step whose viewer may change a line's Account or its
// Department on this invoice (`glCodeEditable`, D-305), held or not (owner,
// 2026-10-03 — D-282).
//
// A BOX ABOVE THE TABLE, ON ITS COLUMNS (D-242): *"I would like the
// Description, Account, Department, Price, Quantity, and Total values to sit
// above where they normally sit, as closely as possible. Maybe once at least
// one row is selected, all of these new fields appear, but the non-applicable
// ones are disabled until they are applicable."* And, on seeing it: *"Could we
// try the hypothetical row inside a box above the table but below the Line
// Items Summary header? Let the table keep its own column headers, and include
// new, identical column headers inside the box with the hypothetical row."*
//
// So this is a bordered box holding a table of its own, drawn from the host's
// own table class, floor and header row (`header`, `tableClassName`,
// `tableMinWidth`), so its columns are the table's columns and cannot drift
// from them. Inside: that header row, the proposed row with one cell per
// column, then the one button — in the place the Reconciles pill and
// *Invoice Subtotal:* used to take (*"we could drop the Reconciles pill and
// Invoice Subtotal piece under the new hypothetical row"*). The host puts the
// box in the same scroller as its table, so the two scroll together.
//
// Two modes, from the picker in the space of the checkbox and # columns
// (*"Since there is nothing to show in the hypothetical row where the
// checkbox and # would normally be, but the Apply/Combine picker there and
// restore the Description field to its usual spot"*). It is the app's own
// `EntityPicker`, the control every other picker in the modal is. It is
// drawn like every other dropdown, not in the brand blue it once had: that
// fill is for real action buttons (owner, 2026-10-14). It shows wherever the
// box does, LOCKED where Update is the only option (owner, 2026-10-14: "the
// action dropdown should always be present but just be locked when there is
// only one option"). The options read Update and Group (Combine until
// 2026-10-23, D-407), and the button Apply (owner, 2026-10-14 — they read
// Apply and Submit until then):
//  - UPDATE writes the Account, and the Department if one was chosen, onto
//    every selected line. Description, Price, Quantity, Total and both icons
//    are disabled. Where the Account is locked and the Department open (the
//    Manager route past Import Control — owner, 2026-10-07, D-305), Update is
//    DEPARTMENT-ONLY (`scope`): the Account shows disabled and empty, as it is
//    not a choice; the Department is required and is the one thing written.
//  - GROUP MAKES A GROUP (owner, 2026-10-22 — D-399, G-20), for two or more
//    lines wherever grouping may change (`groupingOpen`: Import Control, Denial
//    Review, Controller Approval, Export Control). The lines stay, under the
//    group; the Description is the group's name and the one thing required.
//    An Account (with a Department) or a Marketing Category chosen is written
//    to every line in the group (G-7); left out, each line keeps its own. The
//    Total is the sum of the selected totals, the Price that Total and the
//    Quantity 1, all locked (G-6). The Additional Detail pop-up only lists the
//    lines' tags (D-425): they stay on the lines (G-11).
//  - REMOVE FROM GROUP takes the checked lines out of their groups, and MOVE TO
//    GROUP puts them in the group picked in the Description's cell; a moved
//    line keeps its own values (G-20). Neither uses any other field.
//  - DELETE removes every checked line as its own trash button would (owner,
//    2026-10-23 — D-407), offered only where that button is (`onDelete`). It
//    uses no field.
//
// The Department is the rows' own cascade (*"I would expect the relationship
// between these two fields to work basically as it does in the normal Line
// Items Summary section"*): disabled until an Account is chosen, then offering
// the departments that compose with it, Capital Equipment left out under the
// $5,000 floor — across every selected line's total for Apply
// (`loadBulkDepartmentOptions`, the intersection), and at the new line's total
// for Combine. A new Account drops a Department that no longer fits. In
// Department-only Apply it is open at once and offers the departments that
// compose with EVERY selected line's own Account — the same intersection, each
// line with its own Account and total — and nothing while any of them has no
// Account.
//
// The icon column holds a garbage can that unselects every line and closes
// the box (*"use the garbage can as a quick button to unselect all and
// abandon this work"*), in both tables: the read-only table's icon column is
// widened for it wherever lines can be checked (`readOnlyDetailsWidth`).
//
// This component holds the row's own fields and nothing else. What a click
// writes is its caller's: the editor's rows at Import Control, the per-line
// routes past it (`lib/lineItemBulk.ts`).
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  Button,
  Caption1,
  Input,
  Table,
  TableBody,
  TableCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { DeleteRegular } from '@fluentui/react-icons'
import { CODING_PICKER_PLACEHOLDER, EntityPicker, type EntityOption } from './EntityPicker'
import { GroupNamePicker } from './GroupNamePicker'
import { AdditionalDetailPopover } from './AdditionalDetailPopover'
import { loadAssociatedEntities, loadBulkDepartmentOptions, searchAccounts } from '../lib/data'
import { detailCategoryLabel, groupedMoney, toNumber, type LineItemDetailScreen } from '../lib/format'
import { useHalfLabels } from '../lib/hooks'
import { additionalDetailSections } from '@ap/shared'
import {
  associatedEntitiesState,
  isBlank,
  lineDetailIconState,
  additionalDetailEditableAt,
  additionalDetailState,
  showRequiredBorder,
} from '../lib/required'
import { useMoneyInputStyles, useNumberInputStyles, useRequiredStyles } from '../lib/theme'
import { actionA11y } from '../lib/holds'
import type { AssociatedEntity, Invoice, InvoiceLineItem } from '../lib/types'
import {
  BULK_MODE_OPTIONS,
  agreedDetails,
  bulkBlockedReason,
  bulkFieldsEnabled,
  bulkModesOffered,
  combineDefaults,
  sharedAccountId,
  combinedEntities,
  type AccountBulkChoice,
  type BulkApplyScope,
  type BulkChoice,
  type BulkMode,
  type CombineInput,
  type SelectedDetail,
} from '../lib/lineItemBulk'

/** The garbage can's name and tooltip in the bulk row. */
const UNSELECT_ALL_LABEL = 'Unselect All'

/** The icon column's two small icon buttons, 24px each, and the gap between
    them (three until Associated Entities folded into Additional Detail, D-425). */
const ICON_SIZE = 24
const ICON_GAP = 2
const ICON_GROUP_WIDTH = 2 * ICON_SIZE + ICON_GAP

const useStyles = makeStyles({
  // THE BOX (D-242). An OUTLINE, not a border: an outline takes no room, so
  // the box's table is exactly as wide as the table under it and every column
  // lines up to the pixel. Drawn inside the edge (`outlineOffset`) so the
  // scroller does not clip it. The shade sets it apart from the lines. Theme
  // tokens, so it reads in both themes.
  box: {
    outline: `1px solid ${tokens.colorNeutralStroke1}`,
    outlineOffset: '-1px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    marginBottom: '8px',
    width: '100%',
  },
  // Fluent's `TableRow` draws a line under every row and shades it on hover.
  // Neither belongs inside the box: the two rows are one control.
  row: {
    borderBottom: 'none',
    backgroundColor: 'transparent',
    ':hover': { backgroundColor: 'transparent' },
    ':active': { backgroundColor: 'transparent' },
  },
  // A boxed value fills its cell and may shrink inside a fixed-layout table:
  // Fluent's `Input` ships its own `min-width`, which would otherwise set the
  // floor for the narrow numeric columns (the rows' `cellInput`).
  fill: { width: '100%', minWidth: 0 },
  description: { width: '100%', minWidth: 0 },
  icons: { display: 'flex', alignItems: 'center', gap: `${ICON_GAP}px`, justifyContent: 'flex-end' },
  // The button row: the refusal text on the left, the button on the right,
  // where the pill and the subtotal used to be.
  actionCell: { paddingTop: '2px', paddingBottom: '8px' },
  actions: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px' },
  error: { color: tokens.colorPaletteRedForeground1, flexGrow: 1 },
  // AS WIDE AS THE ICONS ABOVE IT — Additional Detail and Unselect All
  // (owner, 2026-10-14: it sat slightly narrower under them, "which is a
  // little bit awkward"; three icons until D-425). A minimum, so *Grouping…*
  // can still grow past it while it runs.
  apply: { minWidth: `${ICON_GROUP_WIDTH}px` },
})

/** One checked line, as the row reads it. */
export interface BulkSelectedLine extends SelectedDetail {
  id: string
  total?: number | string | null
  /** The total the Capital Equipment floor judges: its group's for a line in a
      group (D-403), else its own. Absent: `total`. */
  floorTotal?: number | null
  /** The line's Account now: when every checked line shares one, the row starts from it. */
  accountId?: string | null
  /** The line's Department now: when every checked line shares one too, the row starts from it (D-413). */
  departmentId?: string | null
  /** The group the line sits under, by key, or null (D-399). */
  groupKey?: string | null
  /** False for a line added and not yet saved: its Additional Detail cannot
      be written until the save gives it an id, as on its own row. */
  saved?: boolean
}

/**
 * THE HOST TABLE'S COLUMNS, in its own order, so each field lands in its
 * column: select, #, Description, [Code], Account, [Department],
 * Price, Quantity, [Tax, Shipping], Total, then the icon column.
 */
interface BulkRowColumns {
  code: boolean
  department: boolean
  distributing: boolean
}

interface Props {
  selected: readonly BulkSelectedLine[]
  /** True where grouping may change here (`groupingOpen`, G-12): Combine, Ungroup and Move to Existing Group are offered. */
  grouping: boolean
  /** Every group on the invoice, for Move to Existing Group's picker. */
  groups: readonly { key: string; name: string }[]
  /** The invoice's vendor: Combine offers its established groups (D-402). */
  vendorId?: string | null
  /** What Apply may write here (`bulkApplyScope`): the Account (and a Department), or the Department alone (D-305). */
  scope: BulkApplyScope
  /** Which screen mounted the table: what `lib/required.ts` reads for the red borders. */
  screen: LineItemDetailScreen
  columns: BulkRowColumns
  /** The host table's own header row, as its `TableHeader` — the box shows
      the same headers, from the same code, so they cannot drift (D-242). */
  header: ReactNode
  /** The host table's class and floor, for the same reason. */
  tableClassName?: string
  tableMinWidth: number
  /** The invoice: what the Additional Detail pop-up's Associated Entities read. Absent, that section is not drawn, as on the rows. */
  invoice?: Invoice
  /** A write the caller is running; locks the row. */
  busy?: boolean
  /** The caller's refusal text, shown in the button row. */
  error?: string | null
  mountNode?: HTMLElement | null
  onApply: (choice: BulkChoice) => Promise<void> | void
  /** Absent where grouping never changes. */
  onCombine?: (input: CombineInput) => Promise<void> | void
  /** Delete (D-407): absent where a line's own trash button is not offered. */
  onDelete?: () => Promise<void> | void
  onRemoveFromGroup?: () => Promise<void> | void
  onMoveToGroup?: (groupKey: string) => Promise<void> | void
  onClear: () => void
}

export function LineItemBulkBar({
  selected,
  grouping,
  groups,
  vendorId = null,
  scope,
  screen,
  columns,
  header,
  tableClassName,
  tableMinWidth,
  invoice,
  busy = false,
  error,
  mountNode,
  onApply,
  onCombine,
  onDelete,
  onRemoveFromGroup,
  onMoveToGroup,
  onClear,
}: Props) {
  const s = useStyles()
  const ms = useMoneyInputStyles()
  const ns = useNumberInputStyles()
  const rs = useRequiredStyles()
  const labels = useHalfLabels()

  // THE MODE. The picker always shows; Combine, Ungroup and Move to
  // Group join its options where grouping may change and the checked lines
  // allow them (the shared picker has no disabled option, so until then it
  // offers Update alone, locked).
  const modes = bulkModesOffered({
    grouping: grouping && onCombine !== undefined,
    deletable: onDelete !== undefined,
    selectedGroups: selected.map((r) => r.groupKey ?? null),
    groupKeys: groups.map((g) => g.key),
  })
  const [chosenMode, setChosenMode] = useState<BulkMode>('apply')
  const mode: BulkMode = modes.includes(chosenMode) ? chosenMode : 'apply'
  const [targetGroup, setTargetGroup] = useState<EntityOption | undefined>(undefined)
  const on = bulkFieldsEnabled(mode, scope)
  const departmentOnly = scope === 'department-only'

  const [account, setAccount] = useState<EntityOption | undefined>(undefined)
  const [department, setDepartment] = useState<EntityOption | undefined>(undefined)
  const [description, setDescription] = useState('')

  // COMBINE'S AMOUNTS (D-242): the Total is the sum of the selected totals,
  // the Price that Total, the Quantity 1. Locked.
  const selectedKey = selected.map((r) => `${r.id}:${r.total ?? ''}:${r.floorTotal ?? ''}`).join('|')
  const amounts = useMemo(() => combineDefaults(selected.map((r) => r.total)), [selectedKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // THE DEPARTMENT OPTIONS — the row pickers' cascade, for the lines the
  // choice will land on. Department-only, each line keeps its own Account
  // (D-305), so the options are those every line's Account composes with.
  const [options, setOptions] = useState<EntityOption[] | 'loading'>([])
  const lineAccountsKey = selected.map((r) => r.accountId ?? '').join('|')
  const optionLines = useMemo(() => {
    const total = (r: BulkSelectedLine) =>
      r.floorTotal !== undefined && r.floorTotal !== null
        ? r.floorTotal
        : r.total === null || r.total === undefined || r.total === '' ? null : toNumber(r.total)
    if (departmentOnly) return selected.map((r) => ({ account_id: r.accountId ?? null, total: total(r) }))
    if (account === undefined) return []
    if (mode === 'combine') return [{ account_id: account.id, total: Number(amounts.total) }]
    return selected.map((r) => ({ account_id: account.id, total: total(r) }))
  }, [departmentOnly, account, mode, amounts, selectedKey, lineAccountsKey]) // eslint-disable-line react-hooks/exhaustive-deps
  const optionsKey = optionLines.map((l) => `${l.account_id ?? ''}:${l.total ?? ''}`).join(',')
  useEffect(() => {
    if (optionLines.length === 0) {
      setOptions([])
      return
    }
    let live = true
    setOptions('loading')
    loadBulkDepartmentOptions(optionLines)
      .then((list) => {
        if (live) setOptions(list.map((d) => ({ id: d.department_id, primary: d.department_name, secondary: d.department_id })))
      })
      .catch(() => {
        if (live) setOptions([])
      })
    return () => {
      live = false
    }
  }, [optionsKey]) // eslint-disable-line react-hooks/exhaustive-deps
  // THE ACCOUNT → DEPARTMENT CASCADE, as on a normal line (owner, 2026-09-30
  // — D-242): a new Account clears the Department, even one that would also
  // fit it, and when the new Account composes with exactly one Department
  // that one is filled in. A Department that stops fitting every selected
  // line's total is dropped too.
  function pickAccount(o: EntityOption) {
    if (o.id !== account?.id) setDepartment(undefined)
    setAccountPicked(true)
    setAccount(o)
  }
  // THE SHARED ACCOUNT STARTS THE ROW (owner, 2026-09-30 — D-242): when every
  // checked line already holds the same Account, the row's Account starts as
  // that one, so its Department is open at once for a Department-only Apply.
  // It follows the checked lines until the reviewer picks an Account.
  const [accountPicked, setAccountPicked] = useState(false)
  const shared = sharedAccountId(selected.map((r) => r.accountId))
  useEffect(() => {
    // Department-only, the row holds no Account at all (D-305).
    if (departmentOnly || accountPicked || (account?.id ?? null) === shared) return
    setDepartment(undefined)
    setAccount(shared === null ? undefined : { id: shared, primary: '' })
  }, [shared, accountPicked, departmentOnly]) // eslint-disable-line react-hooks/exhaustive-deps
  // THE SHARED DEPARTMENT STARTS THE ROW TOO (owner, 2026-10-23 — D-413:
  // "grouping assumes the Account when all child-to-be records already share
  // a common Account but does not assume the Department … Shouldn't it?"):
  // while the row's Account is the one the checked lines share, a Department
  // they all share, and the Account admits, is filled in.
  const sharedDepartment = sharedAccountId(selected.map((r) => r.departmentId))
  useEffect(() => {
    if (options === 'loading') return
    if (options.length === 1) {
      setDepartment(options[0])
      return
    }
    setDepartment((prev) => {
      if (prev && options.some((o) => o.id === prev.id)) return prev
      if (!accountPicked && sharedDepartment !== null && (account?.id ?? null) === shared) {
        return options.find((o) => o.id === sharedDepartment)
      }
      return undefined
    })
  }, [options, sharedDepartment]) // eslint-disable-line react-hooks/exhaustive-deps
  const optionList = options === 'loading' ? [] : options

  // THE NEW LINE'S ADDITIONAL DETAIL (D-242): the Marketing Category it owes —
  // by the chosen Account's kind, as a row's — starting from the value every
  // selected line agrees on, reset when the selection changes. A combined line
  // is this app's own, never Fleet Maintenance's (Combine is not offered on a
  // locked invoice, D-380), so it carries no transportation detail.
  const detailKey = selected.map((r) => `${r.id}:${r.marketingCategoryId ?? ''}`).join('|')
  const start = useMemo(() => agreedDetails(selected), [detailKey]) // eslint-disable-line react-hooks/exhaustive-deps
  const [marketing, setMarketing] = useState(start.marketing)
  useEffect(() => {
    setMarketing(start.marketing)
  }, [start])
  // Apply writes no Additional Detail, so its disabled icon owes nothing and
  // never lights up.
  const sections = on.additionalDetail
    ? additionalDetailSections(labels.detailKind(account?.id), null)
    : { marketing: false, transportation: false }
  // A line not yet saved has no detail row to write (its own row says "Save
  // the invoice to fill these in"), so neither does a choice that includes it.
  const detailEditable =
    on.additionalDetail && !busy && additionalDetailEditableAt(screen) && selected.every((r) => r.saved !== false)
  const other = additionalDetailState({
    sections,
    screen,
    editable: { marketing: detailEditable },
    marketingCategoryId: marketing.id,
    hasTransportationDetail: false,
  })

  // THE GROUP'S ASSOCIATED ENTITIES: every selected line's tags, one per
  // entity, listed only — they stay on their lines (G-11). Combine mode only.
  const [tags, setTags] = useState<AssociatedEntity[]>([])
  const invoiceId = invoice?.id
  useEffect(() => {
    if (mode !== 'combine' || !invoiceId) {
      setTags([])
      return
    }
    let live = true
    loadAssociatedEntities(invoiceId)
      .then((rows) => {
        if (live) setTags(combinedEntities(rows, new Set(selected.map((r) => r.id))))
      })
      .catch(() => {
        if (live) setTags([])
      })
    return () => {
      live = false
    }
  }, [mode, invoiceId, selectedKey]) // eslint-disable-line react-hooks/exhaustive-deps
  const entityState = associatedEntitiesState(tags)

  const blocked = bulkBlockedReason({
    mode,
    scope,
    accountId: account?.id,
    departmentId: department?.id,
    description,
    additionalDetailMissing: (mode === 'combine' || mode === 'apply') && !departmentOnly && !!account && other.requiredMissing,
    groupKey: targetGroup?.id,
  })
  // One word for every mode (owner, 2026-09-30 — D-242): the picker on the
  // left already says which. Apply, since 2026-10-14 (it read Submit).
  const label = busy ? (mode === 'combine' ? 'Grouping…' : mode === 'move' ? 'Moving…' : mode === 'delete' ? 'Deleting…' : 'Updating…') : 'Apply'

  async function submit() {
    if (blocked) return
    if (mode === 'remove') {
      await onRemoveFromGroup?.()
      return
    }
    if (mode === 'delete') {
      await onDelete?.()
      return
    }
    if (mode === 'move') {
      if (targetGroup) await onMoveToGroup?.(targetGroup.id)
      setTargetGroup(undefined)
      return
    }
    if (mode === 'combine' && onCombine) {
      // The group's name, and any value chosen, written to every line (G-7).
      const choice: AccountBulkChoice | undefined = account
        ? {
            accountId: account.id,
            accountLabel: account.primary || labels.account(account.id) || account.id,
            ...(department ? { departmentId: department.id, departmentLabel: department.primary } : {}),
          }
        : undefined
      const pickedMarketing =
        account && sections.marketing && marketing.id !== null && marketing.id !== start.marketing.id
          ? { id: marketing.id, label: marketing.label, context: null }
          : undefined
      await onCombine({
        description,
        ...(choice ? { choice } : {}),
        ...(pickedMarketing ? { marketing: pickedMarketing } : {}),
      })
      setDescription('')
      return
    }
    if (departmentOnly) {
      // The Department alone; the Account route is never called (D-305).
      if (department) await onApply({ departmentId: department.id, departmentLabel: department.primary })
      return
    }
    if (!account) return
    // The Marketing Category, as Move to New Group writes it (D-415).
    const pickedCategory =
      sections.marketing && marketing.id !== null && marketing.id !== start.marketing.id
        ? { id: marketing.id, label: marketing.label, context: null }
        : undefined
    const choice: AccountBulkChoice = {
      accountId: account.id,
      accountLabel: account.primary || labels.account(account.id) || account.id,
      ...(department ? { departmentId: department.id, departmentLabel: department.primary } : {}),
      ...(pickedCategory ? { marketing: pickedCategory } : {}),
    }
    await onApply(choice)
  }

  // One cell per host column, in its order.
  const empty = (key: string) => <TableCell key={key} />
  const unselect = (
    <Button
      appearance="subtle"
      size="small"
      icon={<DeleteRegular />}
      aria-label={UNSELECT_ALL_LABEL}
      title={UNSELECT_ALL_LABEL}
      disabled={busy}
      onClick={onClear}
    />
  )
  const modeOptions: EntityOption[] = BULK_MODE_OPTIONS.filter((o) => modes.includes(o.value)).map((o) => ({
    id: o.value,
    primary: o.label,
  }))
  // Move to Existing Group's choices: every group but one already holding every checked line.
  const groupOptions: EntityOption[] = groups
    .filter((g) => !selected.every((r) => (r.groupKey ?? null) === g.key))
    .map((g) => ({ id: g.key, primary: g.name || 'Unnamed group' }))
  const modeLabel = BULK_MODE_OPTIONS.find((o) => o.value === mode)?.label
  const lockedAmount = (value: string, ariaLabel: string, align: 'money' | 'number') => (
    <Input
      className={s.fill}
      size="small"
      type="text"
      aria-label={ariaLabel}
      contentBefore={align === 'money' ? '$' : undefined}
      input={{ className: align === 'money' ? ms.moneyText : ns.numText }}
      value={on.amounts ? value : ''}
      disabled={!on.amounts}
      readOnly
      appearance={on.amounts ? 'filled-darker' : undefined}
    />
  )
  // THE CELLS, one per host column (the picker spans the select and #
  // columns), in the tables' order.
  const cells = [
    <TableCell key="mode" colSpan={2}>
      <EntityPicker
        size="small"
        ariaLabel="What to do with the selected lines"
        value={{ id: mode, name: modeLabel }}
        mountNode={mountNode}
        disabled={busy}
        locked={modes.length === 1}
        optionsKey={modes.join('|')}
        search={(t) => {
          const q = t.trim().toLowerCase()
          return Promise.resolve(q === '' ? modeOptions : modeOptions.filter((o) => o.primary.toLowerCase().includes(q)))
        }}
        onSelect={(o) => setChosenMode(o.id as BulkMode)}
      />
    </TableCell>,
    <TableCell key="description">
      {mode === 'move' ? (
        <EntityPicker
          size="small"
          ariaLabel="Group"
          placeholder="Choose a group"
          value={{ id: targetGroup?.id, name: targetGroup?.primary }}
          mountNode={mountNode}
          disabled={busy}
          invalid={!targetGroup && !busy}
          optionsKey={groupOptions.map((o) => o.id).join('|')}
          search={(t) => {
            const q = t.trim().toLowerCase()
            return Promise.resolve(q === '' ? groupOptions : groupOptions.filter((o) => o.primary.toLowerCase().includes(q)))
          }}
          onSelect={setTargetGroup}
        />
      ) : mode === 'combine' ? (
        // THE GROUP'S NAME (D-402), the vendor's established groups or a new one.
        <GroupNamePicker
          ariaLabel="Description"
          placeholder="e.g. Poultry"
          value={description}
          vendorId={vendorId}
          mountNode={mountNode}
          disabled={busy}
          invalid={showRequiredBorder('description', screen, isBlank(description), !busy)}
          onChange={setDescription}
        />
      ) : (
        <Input
          size="small"
          aria-label="Description"
          className={mergeClasses(
            s.description,
            showRequiredBorder('description', screen, isBlank(description), on.description && !busy) && rs.requiredEmpty,
          )}
          value={on.description ? description : ''}
          disabled={!on.description || busy}
          onChange={(_, d) => setDescription(d.value)}
        />
      )}
    </TableCell>,
    ...(columns.code ? [empty('code')] : []),
    <TableCell key="account">
      <EntityPicker
        size="small"
        ariaLabel="Account"
        placeholder={CODING_PICKER_PLACEHOLDER}
        value={{ id: account?.id, name: account ? account.primary || labels.account(account.id) : undefined }}
        mountNode={mountNode}
        disabled={!on.account || busy}
        invalid={mode === 'apply' && on.account && showRequiredBorder('account', screen, !account, !busy)}
        search={(t) =>
          searchAccounts(t).then<EntityOption[]>((es) =>
            es.map((e) => ({ id: e.account_id, primary: e.account_name, secondary: e.account_id })),
          )
        }
        onSelect={pickAccount}
        // Emptied, the row's Account and Department clear (D-295).
        onClear={() => {
          setAccountPicked(true)
          setAccount(undefined)
          setDepartment(undefined)
        }}
      />
    </TableCell>,
    ...(columns.department
      ? [
          <TableCell key="department">
            <EntityPicker
              size="small"
              ariaLabel="Department"
              value={{ id: department?.id, name: department?.primary }}
              mountNode={mountNode}
              placeholder={CODING_PICKER_PLACEHOLDER}
              disabled={!on.department || busy || (!departmentOnly && !account) || (options !== 'loading' && optionList.length === 0)}
              optionsKey={optionsKey}
              search={(term) => {
                const q = term.trim().toLowerCase()
                return Promise.resolve(
                  q === '' ? optionList : optionList.filter((o) => `${o.primary} ${o.secondary ?? ''}`.toLowerCase().includes(q)),
                )
              }}
              onSelect={setDepartment}
            />
          </TableCell>,
        ]
      : []),
    <TableCell key="price">{lockedAmount(groupedMoney(amounts.price), 'Price', 'money')}</TableCell>,
    <TableCell key="quantity">{lockedAmount(amounts.quantity, 'Quantity', 'number')}</TableCell>,
    ...(columns.distributing ? [empty('tax'), empty('shipping')] : []),
    <TableCell key="total">{lockedAmount(groupedMoney(amounts.total), 'Total', 'money')}</TableCell>,
    <TableCell key="icons">
      <div className={s.icons}>
        {/* ONE ICON (D-425): the Marketing Category the new group owes, and
            the selected lines' tags, listed read-only. */}
        <AdditionalDetailPopover
          mountNode={mountNode}
          disabled={!on.additionalDetail || busy}
          sections={sections}
          state={lineDetailIconState(other, entityState)}
          marketing={{
            categoryId: marketing.id,
            label: marketing.label,
            onSelect: detailEditable
              ? (o) => setMarketing({ id: o.id, label: detailCategoryLabel(o.id, o.primary, o.secondary) })
              : undefined,
          }}
          entities={
            invoice
              ? {
                  invoice,
                  lineItem: { id: selected[0]?.id ?? '' } as InvoiceLineItem,
                  lineItemIds: selected.map((r) => r.id),
                  childMode: 'union',
                  editable: false,
                }
              : undefined
          }
        />
        {unselect}
      </div>
    </TableCell>,
  ]

  // The picker's cell spans two columns.
  const columnCount = cells.length + 1

  return (
    <div className={s.box} style={{ minWidth: tableMinWidth }}>
      <Table size="small" aria-label="Selected lines" className={tableClassName} style={{ minWidth: tableMinWidth }}>
        {header}
        <TableBody>
      <TableRow className={s.row}>{cells}</TableRow>
      <TableRow className={s.row}>
        <TableCell colSpan={columnCount} className={s.actionCell}>
          <div className={s.actions}>
            {error && <Caption1 className={s.error}>{error}</Caption1>}
            <Button
              size="small"
              appearance="primary"
              className={s.apply}
              disabled={!!blocked || busy}
              title={blocked}
              {...actionA11y(label, { reason: blocked })}
              onClick={() => {
                void submit()
              }}
            >
              {label}
            </Button>
          </div>
        </TableCell>
      </TableRow>
        </TableBody>
      </Table>
    </div>
  )
}
